import { useAppDispatch, useAppSelector } from '@/hooks';
import { useArchiveDocumentMutation, useUpdateDocumentTypeMutation } from '@/services/documents';
import { addAttachments } from '@/store/reducers/flaggingModeSlice';
import { Box, Button, CheckboxInput, Accordion, SelectInput, toast } from '@palmetto/palmetto-components';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AllDocumentTypes, DocumentDocument, documentTitles } from 'types';

import { ReviewPackagePhotoCategory } from '../../../types/ReviewPackage';
import { DocumentPreview } from '../../documents/DocumentPreviewModal';

import ArchiveConfirmationModal from './ArchiveConfirmationModal';
import './CheckboxHeader.scss';

interface CheckboxHeaderProps {
  documents: (DocumentPreview | DocumentDocument)[];
  checkboxCategories: Array<AllDocumentTypes>;
  allowArchive: boolean;
  checkedItems: string[];
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>;
}

export default function CheckboxHeader({
  documents,
  checkboxCategories,
  allowArchive,
  checkedItems,
  setCheckedItems,
}: CheckboxHeaderProps) {
  const { id } = useParams<{ id: any }>();
  const dispatch = useAppDispatch();
  const {
    isActive: isFlaggingModeActive,
    flagLabel,
    allowedDocumentTypes,
    hasDecisionRendered,
  } = useAppSelector((state) => state.flaggingMode);

  const eligibleCheckedItems = useMemo(
    () =>
      checkedItems.filter((checkedId) => {
        const doc = documents.find((d) => {
          const docIdStr = d.id?.toString() || '';
          const dashIndex = docIdStr.indexOf('-');
          const sliceIndex = dashIndex > -1 ? dashIndex : docIdStr.length;
          return docIdStr.slice(0, sliceIndex) === checkedId;
        });
        return !!doc && allowedDocumentTypes.includes((doc as DocumentDocument).type);
      }),
    [checkedItems, documents, allowedDocumentTypes],
  );

  const showAttachToFlag = isFlaggingModeActive && !hasDecisionRendered && eligibleCheckedItems.length > 0;

  const handleAttachToFlag = () => {
    const selected = eligibleCheckedItems
      .map((checkedId) => {
        const doc = documents.find((d) => {
          const docIdStr = d.id?.toString() || '';
          const dashIndex = docIdStr.indexOf('-');
          const sliceIndex = dashIndex > -1 ? dashIndex : docIdStr.length;
          return docIdStr.slice(0, sliceIndex) === checkedId;
        });
        if (!doc) return null;
        const fileNames = (doc as DocumentDocument).files.map((f) => f.originalName);
        const name = fileNames.length > 0 ? fileNames : [doc.id?.toString() || checkedId];
        return { id: checkedId, name };
      })
      .filter((a): a is { id: string; name: string[] } => a !== null);
    dispatch(addAttachments(selected));
    setCheckedItems([]);
    toast.success(`${selected.length} document${selected.length === 1 ? '' : 's'} attached to flag`);
  };
  const [selectedCategory, setSelectedCategory] = useState<{
    value: ReviewPackagePhotoCategory;
    label: string;
  } | null>(null);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [updateDocumentType] = useUpdateDocumentTypeMutation();
  const [archiveDocument] = useArchiveDocumentMutation();

  const moveDocuments = async (documentIds: string[], type: ReviewPackagePhotoCategory) => {
    const changedDocuments = getChangedDocuments(documentIds);

    try {
      return await Promise.all(
        changedDocuments.map((doc) =>
          updateDocumentType({
            id: doc.id,
            accountId: id,
            type,
          }),
        ),
      );
    } catch (e) {
      console.error(`Error updating type for documents: ${e}`);
      toast.error('An error occurred while moving documents');
    }
  };
  const archiveDocuments = async (documentIds: string[]) => {
    const changedDocuments = getChangedDocuments(documentIds);

    try {
      return await Promise.all(
        changedDocuments.map((doc) =>
          archiveDocument({
            id: doc.id,
            accountId: id,
            invalidateAllTags: false,
          }),
        ),
      );
    } catch (e) {
      console.error(`Error archiving documents: ${e}`);
      toast.error('An error occurred while archiving documents');
    }
  };

  // helper to sanitize doc ids that have been modified with an index suffix
  function getChangedDocuments(documentIds: string[]) {
    return documents
      .map((doc) => {
        const docIdStr = doc.id?.toString() || '';
        const dashIndex = docIdStr.indexOf('-');
        const sliceIndex = dashIndex > -1 ? dashIndex : docIdStr.length;
        const slicedDocId = docIdStr.slice(0, sliceIndex);
        return { ...doc, id: slicedDocId };
      })
      .filter((doc) => documentIds.includes(doc.id));
  }

  const moveOptions = useMemo(
    () =>
      checkboxCategories
        .map((value) => ({
          value,
          label: documentTitles[value],
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [checkboxCategories],
  );

  return (
    <Box
      className="checkbox-header"
      position="sticky"
      background="primary"
      borderColor="separator"
      justifyContent="space-between"
      alignItems="center"
      padding="sm"
      style={{
        flexDirection: 'row',
        zIndex: 1,
      }}
    >
      <Box alignItems="center" style={{ flexDirection: 'row' }}>
        <CheckboxInput
          id="checkbox-header"
          className="checkbox"
          label={`${checkedItems.length} selected`}
          justifyContent="center"
          alignItems="center"
          flexDirection="row"
          style={{ padding: '0.5em 1em' }}
          isChecked={checkedItems.length === documents.length}
          isIndeterminate={!!checkedItems.length && checkedItems.length !== documents.length}
          onChange={() => {
            setCheckedItems([]);
          }}
        />
        <Box
          as="span"
          tabIndex={0}
          fontWeight="bold"
          cursor="pointer"
          textAlign="center"
          style={{
            padding: '0.5em 1em',
            whiteSpace: 'nowrap',
          }}
          onClick={() => {
            setCheckedItems(documents.map((doc) => doc.id?.toString() || ''));
          }}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setCheckedItems(documents.map((doc) => doc.id?.toString() || ''));
            }
          }}
        >
          Select All
        </Box>
      </Box>
      <Box
        background="primary"
        position="relative"
        alignItems="center"
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          zIndex: 1,
        }}
      >
        {showAttachToFlag && (
          <Box
            as="span"
            tabIndex={0}
            fontWeight="bold"
            cursor="pointer"
            textAlign="center"
            style={{
              padding: '0.5em 1em',
              whiteSpace: 'nowrap',
            }}
            onClick={handleAttachToFlag}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleAttachToFlag();
              }
            }}
          >
            Attach to Flag: {flagLabel}
          </Box>
        )}
        <Accordion background="primary" position="relative">
          <Accordion.Panel isOpen={isMoveOpen} style={{ borderBottom: 'none' }}>
            <Accordion.PanelSummary
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="row"
              background="primary"
              style={{
                padding: '0.5em 1em',
                whiteSpace: 'nowrap',
              }}
              isDetailsOpen={isMoveOpen}
              onToggle={() => setIsMoveOpen(!isMoveOpen)}
            >
              Move
            </Accordion.PanelSummary>
            <Accordion.PanelDetails
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              position="absolute"
              padding="sm"
              gap="lg"
              background="primary"
              className="photos-list-move-dropdown"
              style={{
                width: '250px',
                right: 0,
                top: '100%',
                zIndex: 2,
              }}
            >
              <SelectInput
                size="md"
                id="move-select"
                name="move-select"
                label=""
                isSearchable
                menuShouldScrollIntoView={true}
                onChange={(e) => setSelectedCategory(e.target.value)}
                value={selectedCategory}
                options={moveOptions}
                isClearable={true}
              />
              <Button
                fullWidth
                size="sm"
                isDisabled={!selectedCategory}
                onClick={() => {
                  if (selectedCategory) {
                    moveDocuments(checkedItems, selectedCategory.value);
                    setCheckedItems([]);
                    setSelectedCategory(null);
                    setIsMoveOpen(false);
                  }
                }}
              >
                Move
              </Button>
            </Accordion.PanelDetails>
          </Accordion.Panel>
        </Accordion>
        {allowArchive && (
          <Box
            as="span"
            tabIndex={0}
            fontWeight="bold"
            cursor="pointer"
            textAlign="center"
            style={{
              padding: '0.5em 1em',
              whiteSpace: 'nowrap',
            }}
            onClick={() => {
              setIsArchiveModalOpen(true);
            }}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsArchiveModalOpen(true);
              }
            }}
          >
            Archive
          </Box>
        )}
        <ArchiveConfirmationModal
          isOpen={isArchiveModalOpen}
          onClose={() => setIsArchiveModalOpen(false)}
          onConfirm={() => {
            archiveDocuments(checkedItems);
            setCheckedItems([]);
          }}
        />
      </Box>
    </Box>
  );
}
